import { analytics } from "./analytics";
import { isBrowser } from "./browser";
import {
  FREE_15_MINUTE_INTERVIEW_PAGE_TYPE,
  type MockInterviewEntry,
  type MockInterviewFunnelSetupStep,
} from "./events";

const FREE_INTERVIEW_META_PREFIX = "ml_free_interview_meta:";

export type InterviewStartAnalyticsInput = {
  interviewId: string;
  isFreeMock?: boolean | null;
  interviewType?: string | null;
  durationMinutes?: number | null;
};

function metaKey(interviewId: string): string {
  return `${FREE_INTERVIEW_META_PREFIX}${interviewId}`;
}

/** Persist free-mock metadata so completion/feedback can reuse it later in the session. */
export function rememberInterviewAnalyticsMeta(
  input: InterviewStartAnalyticsInput,
): void {
  if (!isBrowser() || !input.interviewId) return;
  try {
    window.sessionStorage.setItem(
      metaKey(input.interviewId),
      JSON.stringify({
        isFreeMock: Boolean(input.isFreeMock),
        interviewType: input.interviewType ?? "resume_based",
        durationMinutes: input.durationMinutes ?? undefined,
      }),
    );
  } catch {
    // ignore storage failures
  }
}

export function readInterviewAnalyticsMeta(interviewId: string | null | undefined): {
  isFreeMock: boolean;
  interviewType?: string;
  durationMinutes?: number;
} | null {
  if (!isBrowser() || !interviewId) return null;
  try {
    const raw = window.sessionStorage.getItem(metaKey(interviewId));
    if (!raw) return null;
    return JSON.parse(raw) as {
      isFreeMock: boolean;
      interviewType?: string;
      durationMinutes?: number;
    };
  } catch {
    return null;
  }
}

/**
 * Fire after POST /interview/start succeeds.
 * Free-mock interviews use interviewMode "free"; others use "paid".
 */
export function trackInterviewStarted(input: InterviewStartAnalyticsInput): void {
  try {
    rememberInterviewAnalyticsMeta(input);
    const isFree = Boolean(input.isFreeMock);
    analytics.track("interview_started", {
      interviewMode: isFree ? "free" : "paid",
      interviewType: input.interviewType ?? "resume_based",
      durationMinutes:
        typeof input.durationMinutes === "number"
          ? input.durationMinutes
          : isFree
            ? 15
            : undefined,
      ...(isFree ? { plan: "skill_assessment" } : {}),
    });
  } catch {
    // Analytics must never break product flows.
  }
}

export function trackInterviewCompleted(interviewId: string | null | undefined): void {
  try {
    const meta = readInterviewAnalyticsMeta(interviewId);
    // Prefer free-mock funnel; if meta missing, still emit a minimal completed event.
    const isFree = meta?.isFreeMock ?? false;
    analytics.track("interview_completed", {
      interviewMode: isFree ? "free" : "paid",
      interviewType: meta?.interviewType ?? "resume_based",
      durationMinutes: meta?.durationMinutes,
      ...(isFree ? { plan: "skill_assessment" } : {}),
    });
  } catch {
    // ignore
  }
}

export function trackFeedbackGenerated(interviewId: string | null | undefined): void {
  try {
    const meta = readInterviewAnalyticsMeta(interviewId);
    const isFree = meta?.isFreeMock ?? false;
    analytics.track("feedback_generated", {
      interviewMode: isFree ? "free" : "paid",
      interviewType: meta?.interviewType ?? "resume_based",
    });
  } catch {
    // ignore
  }
}

export function trackCheckoutStarted(input: {
  plan: string;
  source?: string;
}): void {
  try {
    const source =
      input.source ??
      (input.plan === "skill_assessment"
        ? FREE_15_MINUTE_INTERVIEW_PAGE_TYPE
        : input.plan);
    analytics.track("checkout_started", {
      source,
      plan: input.plan,
    });
  } catch {
    // ignore
  }
}

export function trackMockInterviewFunnelStep(
  step: MockInterviewFunnelSetupStep,
  properties?: { entry?: MockInterviewEntry },
): void {
  try {
    analytics.track("mock_interview_funnel_step", { step, ...(properties ?? {}) });
  } catch {
    // ignore
  }
}

/** Existing `signup` event. Fire next to GA `sign_up_successful`. */
export function trackSignup(): void {
  try {
    analytics.track("signup");
  } catch {
    // ignore
  }
}

/**
 * Fire next to GA `loginSuccess`, on every successful authentication.
 *
 * `login` has been in the event contract, the allowlist and the dashboard's KPI
 * list since the beginning, and nothing ever emitted it: the tile read zero by
 * construction, and so did anything else keyed on the event name.
 *
 * It matters more than a tile. This is the moment an anonymous browser becomes a
 * known person, so it is the one event that reliably carries both `anonymousId`
 * and `userId` -- which is exactly what ingestion needs to write the identity link
 * the funnel resolves pre-login events through. Before this, that link could only
 * be observed when a returning visitor happened to trigger some other tracked
 * event while signed in; `trackSignup` covers first-time users only.
 *
 * Fired for new accounts too, alongside `signup`, matching what GA already does
 * here. Suppressing it for new users would reintroduce the gap for anyone whose
 * signup event failed to send.
 */
export function trackLogin(): void {
  try {
    analytics.track("login");
  } catch {
    // ignore
  }
}
